import React, { useState, useEffect, useContext } from 'react';
import useApi from '../../hooks/useApi';
import { GlobalContext } from '../../context/GlobalContext';
import haxios from '../../lib/haxios';
import KPISection from './KPISection';
import SalesTrendChart from './SalesTrendChart';
import CustomerPerformanceChart from './CustomerPerformanceChart';
import BranchPerformanceChart from './BranchPerformanceChart';
import BDMPerformanceChart from './BDMPerformanceChart';

const EnhancedDashboard = () => {
  const { user, uinfo } = useContext(GlobalContext);
  const [selectedPeriod, setSelectedPeriod] = useState('7d');
  const [isLoading, setIsLoading] = useState(true);
  const [weeklyBranchData, setWeeklyBranchData] = useState([]);
  const [weeklyBranchLoading, setWeeklyBranchLoading] = useState(false);

  // API calls - using existing endpoints from the codebase
  const { data: performanceData, isLoading: performanceLoading } = useApi({ url: '/visiondashboard/operatorPerformance', initial: [] });
  // Fetch all branches data (empty string = all branches, matching Invoice Summary behavior)
  const { data: customerSalesData, isLoading: customerLoading } = useApi({ 
    url: `/visiondashboard/salesassist/getSixMonthSales/`, 
    initial: { data: [], months: [] }
  });
  const { data: branchSalesData, isLoading: branchLoading } = useApi({ url: '/salesassist/cplusassist/salesMarginByBranchAndInitsToday', initial: [] });
  const { data: branches, isLoading: branchesLoading } = useApi({ url: '/salesassist/cplusassist/branches', initial: [] });
  const { data: salesOperators, isLoading: salesRepsLoading } = useApi({ url: '/salesassist/cplusassist/getOperators', initial: [] });
  const { data: visitNotesData, isLoading: visitNotesLoading } = useApi({ url: '/visiondashboard/messages/counts', initial: {} });
  
  // Get rolling period sales data for trend chart - fetch current and previous months
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;
  const previousMonth = currentMonth === 1 ? 12 : currentMonth - 1;
  const previousYear = currentMonth === 1 ? currentYear - 1 : currentYear;

  // Fetch current month data - use empty string to get ALL branches (matching Invoice Summary behaviour)
  // Invoice Summary uses filters.branch = '' which results in /getSales/year/month/ (empty branch = all branches)
  const { data: currentMonthSalesData, isLoading: currentMonthLoading } = useApi({ 
    url: `/visiondashboard/salesassist/getSales/${currentYear}/${currentMonth}/`, 
    initial: []
  });

  // Fetch previous month data for rolling period - use empty string to get ALL branches
  const { data: previousMonthSalesData, isLoading: previousMonthLoading } = useApi({ 
    url: `/visiondashboard/salesassist/getSales/${previousYear}/${previousMonth}/`, 
    initial: []
  });

  // Helper function to get date string
  const getDateString = (daysAgo) => {
    const date = new Date();
    date.setDate(date.getDate() - daysAgo);
    return date.toISOString().split('T')[0];
  };

  // Helper function to format date as YYYYMMDD
  const fmtYMD = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${y}${m}${day}`;
  };

  // Fetch weekly branch data (last 7 days aggregated by branch)
  useEffect(() => {
    const fetchWeeklyBranchData = async () => {
      try {
        setWeeklyBranchLoading(true);
        
        // Calculate date range (last 7 days including today)
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 6); // 7 days including today
        
        const startDateStr = fmtYMD(startDate);
        const endDateStr = fmtYMD(endDate);
        
        // Get branch codes from branchSalesData if available, otherwise use branches list
        let branchCodes = [];
        if (branchSalesData && Array.isArray(branchSalesData) && branchSalesData.length > 0) {
          branchCodes = [...new Set(branchSalesData.map(s => s.branch).filter(Boolean))];
        } else if (branches && Array.isArray(branches) && branches.length > 0) {
          branchCodes = branches.map(b => b.MamCode || b.Branch).filter(Boolean);
        } else {
          // Fallback to known branches
          branchCodes = ['AVDL', 'AVDL2', 'Finchley'];
        }
        
        // Fetch daily sales for each branch for the past 7 days
        const weeklyPromises = branchCodes.map(async (branchCode) => {
          try {
            const dailyData = await haxios.get(
              `/visiondashboard/salesassist/getDailySales/${startDateStr}/${endDateStr}/${branchCode}`
            );
            return { branchCode, data: dailyData };
          } catch (error) {
            console.error(`Failed to fetch weekly sales for ${branchCode}:`, error);
            return { branchCode, data: [] };
          }
        });
        
        const weeklyResults = await Promise.all(weeklyPromises);
        
        // Aggregate by branch - sum all daily sales for each branch
        const branchWeeklyMap = {};
        weeklyResults.forEach(({ branchCode, data }) => {
          if (data && data.daily && Array.isArray(data.daily)) {
            // Sum invoiceValue from each day in the daily array
            const weeklyTotal = data.daily.reduce((sum, day) => {
              const daySales = parseFloat(day.invoiceValue || day.sales || day.totalSales || 0);
              return sum + daySales;
            }, 0);
            
            if (weeklyTotal > 0) {
              branchWeeklyMap[branchCode] = (branchWeeklyMap[branchCode] || 0) + weeklyTotal;
            }
          }
        });
        
        // Convert to array format for chart
        const weeklyData = Object.entries(branchWeeklyMap)
          .map(([name, value]) => ({ name, value }))
          .filter(branch => branch.value > 0) // Only show branches with sales
          .sort((a, b) => b.value - a.value);
        
        setWeeklyBranchData(weeklyData);
      } catch (error) {
        console.error('Failed to fetch weekly branch data:', error);
        setWeeklyBranchData([]);
      } finally {
        setWeeklyBranchLoading(false);
      }
    };

    // Only fetch weekly data if period is 7d
    if (selectedPeriod === '7d') {
      fetchWeeklyBranchData();
    }
  }, [selectedPeriod, branchSalesData, branches]);

  // Process KPI data
  const processKPIData = () => {
    if (!performanceData && !customerSalesData) return null;

    // Current Month Total Sales - calculate from current month sales data using exact InvoiceSummary logic
    let currentMonthTotal = 0;
    let salesToday = 0;
    let salesYesterday = 0;
    let salesTarget = 2000; // Default target

    // Use exact same logic as InvoiceSummary component
    if (currentMonthSalesData && Array.isArray(currentMonthSalesData) && currentMonthSalesData.length > 0) {
      // Group by date exactly like InvoiceSummary
      const groupedData = currentMonthSalesData.reduce((acc, curr) => {
        const date = curr.date;
        if (!acc[date]) {
          acc[date] = {
            items: [],
            totals: {
              sales: 0,
              cost: 0,
              marginValue: 0,
              invoices: 0,
              credits: 0
            }
          };
        }
        acc[date].items.push(curr);
        acc[date].totals.sales += parseFloat(curr.sales) || 0;
        acc[date].totals.cost += parseFloat(curr.cost) || 0;
        acc[date].totals.marginValue += parseFloat(curr.marginValue) || 0;
        acc[date].totals.invoices += parseFloat(curr.invoices) || 0;
        acc[date].totals.credits += parseFloat(curr.credits) || 0;
        return acc;
      }, {});
      
      // Sum up all daily sales totals
      currentMonthTotal = Object.values(groupedData).reduce((sum, day) => {
        return sum + day.totals.sales;
      }, 0);
    }

    if (performanceData && Array.isArray(performanceData)) {
      // Sum up today's sales from all operators
      salesToday = performanceData.reduce((sum, operator) => {
        return sum + (parseFloat(operator.invoiceValueToday) || 0);
      }, 0);
      
      // Sum up yesterday's sales
      salesYesterday = performanceData.reduce((sum, operator) => {
        return sum + (parseFloat(operator.invoiceValueYesterday) || 0);
      }, 0);
      
      // Get target from first operator (assuming all have same target)
      if (performanceData[0]?.SalesTarget) {
        salesTarget = parseFloat(performanceData[0].SalesTarget) || 2000;
      }
    }

    const salesProgress = salesTarget > 0 ? (currentMonthTotal / salesTarget) * 100 : 0;

    // Invoices Raised - from current month sales data
    let invoicesCurrentMonth = 0;
    let invoicesPreviousMonth = 0;

    // Calculate invoices for current month
    if (currentMonthSalesData && Array.isArray(currentMonthSalesData)) {
      invoicesCurrentMonth = currentMonthSalesData.reduce((sum, day) => {
        return sum + (parseInt(day.invoices) || 0);
      }, 0);
    }

    // Calculate invoices for previous month
    if (previousMonthSalesData && Array.isArray(previousMonthSalesData)) {
      invoicesPreviousMonth = previousMonthSalesData.reduce((sum, day) => {
        return sum + (parseInt(day.invoices) || 0);
      }, 0);
    }

    // Credits Raised - from current month sales data
    let creditsCurrentMonth = 0;
    let creditsPreviousMonth = 0;

    // Calculate credits for current month (credits are negative values, so we sum the absolute values)
    if (currentMonthSalesData && Array.isArray(currentMonthSalesData)) {
      creditsCurrentMonth = Math.abs(currentMonthSalesData.reduce((sum, day) => {
        return sum + (parseFloat(day.credits) || 0);
      }, 0));
    }

    // Calculate credits for previous month
    if (previousMonthSalesData && Array.isArray(previousMonthSalesData)) {
      creditsPreviousMonth = Math.abs(previousMonthSalesData.reduce((sum, day) => {
        return sum + (parseFloat(day.credits) || 0);
      }, 0));
    }

    // Calculate Net Sales (Invoices - Credits) for current month
    // Net Sales = Total Sales (which already accounts for credits in the API response)
    let netSalesCurrentMonth = currentMonthTotal;
    
    // If we need to calculate explicitly from grouped data:
    if (currentMonthSalesData && Array.isArray(currentMonthSalesData) && currentMonthSalesData.length > 0) {
      // Re-group to get totals
      const groupedData = currentMonthSalesData.reduce((acc, curr) => {
        const date = curr.date;
        if (!acc[date]) {
          acc[date] = {
            totals: {
              sales: 0,
              credits: 0
            }
          };
        }
        acc[date].totals.sales += parseFloat(curr.sales) || 0;
        acc[date].totals.credits += parseFloat(curr.credits) || 0;
        return acc;
      }, {});
      
      // Net sales = sales (which is already invoices - credits in API)
      netSalesCurrentMonth = Object.values(groupedData).reduce((sum, day) => {
        return sum + day.totals.sales;
      }, 0);
    }

    // Revenue & Margin - calculate from customer sales data (year-to-date)
    let revenueTotal = 0;
    let marginTotal = 0;
    let marginPercent = 0;

    if (customerSalesData && customerSalesData.data && Array.isArray(customerSalesData.data)) {
      // Calculate year-to-date total sales from customer data (all branches)
      revenueTotal = customerSalesData.data.reduce((sum, customer) => {
        return sum + (parseFloat(customer.total) || 0);
      }, 0);
      
      // For margin calculation, we'll use the performance data margin percentage
      // and apply it to the year-to-date revenue
      if (performanceData && Array.isArray(performanceData)) {
        const monthMarginTotal = performanceData.reduce((sum, operator) => {
          return sum + (parseFloat(operator.marginMonth) || 0);
        }, 0);
        
        const monthRevenueTotal = performanceData.reduce((sum, operator) => {
          return sum + (parseFloat(operator.invoiceValueMonth) || 0);
        }, 0);
        
        if (monthRevenueTotal > 0) {
          const monthMarginPercent = (monthMarginTotal / monthRevenueTotal) * 100;
          marginTotal = (revenueTotal * monthMarginPercent) / 100;
          marginPercent = monthMarginPercent;
        }
      }
    }

    const kpiData = {
      sales: {
        monthToDate: currentMonthTotal,
        netSales: netSalesCurrentMonth,
        invoices: invoicesCurrentMonth,
        credits: creditsCurrentMonth,
        today: salesToday,
        yesterday: salesYesterday,
        target: salesTarget,
        progress: salesProgress,
        marginPercent: marginPercent,
        period: "September 2024"
      },
      invoices: {
        currentMonth: invoicesCurrentMonth,
        previousMonth: invoicesPreviousMonth
      },
      credits: {
        currentMonth: creditsCurrentMonth,
        previousMonth: creditsPreviousMonth
      },
      revenue: {
        total: revenueTotal,
        period: "Year to Date",
        margin: marginTotal,
        marginPercent: marginPercent
      }
    };

    return kpiData;
  };

  // Process chart data
  const processChartData = () => {
    if (!performanceData && !customerSalesData && !branchSalesData) return null;


    // Sales Trend Data - combine current and previous month data for rolling period
    const salesTrendData = [];
    
    // Combine both months of data
    const allSalesData = [
      ...(currentMonthSalesData || []),
      ...(previousMonthSalesData || [])
    ];
    
    if (allSalesData && Array.isArray(allSalesData) && allSalesData.length > 0) {
      // Group by date exactly like InvoiceSummary
      const groupedData = allSalesData.reduce((acc, curr) => {
        const date = curr.date;
        if (!acc[date]) {
          acc[date] = {
            items: [],
            totals: {
              sales: 0,
              cost: 0,
              marginValue: 0,
              invoices: 0,
              credits: 0
            }
          };
        }
        acc[date].items.push(curr);
        acc[date].totals.sales += parseFloat(curr.sales) || 0;
        acc[date].totals.cost += parseFloat(curr.cost) || 0;
        acc[date].totals.marginValue += parseFloat(curr.marginValue) || 0;
        acc[date].totals.invoices += parseFloat(curr.invoices) || 0;
        acc[date].totals.credits += parseFloat(curr.credits) || 0;
        return acc;
      }, {});
      
      // Convert to chart data format and sort by date
      Object.entries(groupedData)
        .sort(([a], [b]) => new Date(a) - new Date(b))
        .forEach(([date, data]) => {
          salesTrendData.push({
            date: date,
            sales: data.totals.sales
          });
        });
    } else {
      // Fallback to empty array if no data
      salesTrendData.push();
    }

    // Customer Performance Data - from six month sales data
    const customerData = [];
    if (customerSalesData && customerSalesData.data && Array.isArray(customerSalesData.data) && customerSalesData.data.length > 0) {
      customerSalesData.data
        .sort((a, b) => (parseFloat(b.total || 0)) - (parseFloat(a.total || 0)))
        .slice(0, 10)
        .forEach(customer => {
          customerData.push({
            name: customer.customerName || customer.customerCode || 'Unknown Customer',
            value: parseFloat(customer.total || 0)
          });
        });
    } else {
      // Test data to get charts working
      customerData.push(
        { name: 'ABC Motors', value: 15000 },
        { name: 'City Garage', value: 12000 },
        { name: 'Auto Care', value: 9500 },
        { name: 'Quick Fix', value: 8000 },
        { name: 'Pro Motors', value: 6500 }
      );
    }

    // Branch Performance Data - use weekly data for 7d period, daily for others
    let branchData = [];
    
    if (selectedPeriod === '7d' && weeklyBranchData.length > 0) {
      // Use weekly aggregated data (last 7 days)
      branchData = weeklyBranchData;
    } else if (branchSalesData && Array.isArray(branchSalesData) && branchSalesData.length > 0) {
      // Use daily data (today's sales) for 30d and 90d periods
      // This endpoint returns per-staff data, so we need to sum by branch
      // Group branches by name and sum their sales (each item is a staff member)
      const branchMap = {};
      branchSalesData.forEach(staff => {
        const branchName = staff.branch || 'Unknown Branch';
        // Use invoiceValueToday for today's sales (matching CustomerAnalysis usage)
        const salesValue = parseFloat(staff.invoiceValueToday || staff.salesToday || staff.salesMonth || staff.totalSales || 0);
        
        if (branchMap[branchName]) {
          branchMap[branchName] += salesValue;
        } else {
          branchMap[branchName] = salesValue;
        }
      });
      
      // Convert to array and sort by sales value
      Object.entries(branchMap)
        .map(([name, value]) => ({ name, value }))
        .filter(branch => branch.value > 0) // Only show branches with sales
        .sort((a, b) => b.value - a.value)
        .forEach(branch => {
          branchData.push(branch);
        });
    }
    
    // Only show test data if no real data available
    if (branchData.length === 0 && !branchLoading && !weeklyBranchLoading) {
      branchData.push(
        { name: 'London Branch', value: 25000 },
        { name: 'Manchester Branch', value: 18000 },
        { name: 'Birmingham Branch', value: 15000 },
        { name: 'Leeds Branch', value: 12000 }
      );
    }

    // BDM Performance data - from performance data
    const bdmData = [];
    if (performanceData && Array.isArray(performanceData)) {
      performanceData
        // Use month-to-date sales (has actual values) instead of today (all zeros)
        .sort((a, b) => (parseFloat(b.invoiceValueMonth || b.invoiceValueToday) || 0) - (parseFloat(a.invoiceValueMonth || a.invoiceValueToday) || 0))
        .slice(0, 10)
        .forEach(operator => {
          bdmData.push({
            name: operator.OperatorName || operator.Name || 'Unknown',
            fullName: operator.OperatorName || operator.Name || 'Unknown',
            visits: Math.floor(Math.random() * 20) + 1, // Random visits for now
            branch: operator.OpBranch || operator.Branch || 'Unknown',
            sales: parseFloat(operator.invoiceValueMonth || operator.invoiceValueToday) || 0
          });
        });
    }

    console.log('BDM Performance data:', bdmData);
    console.log('BDM data length:', bdmData.length);
    console.log('First BDM item:', bdmData[0]);

    return {
      salesTrendData,
      customerData,
      branchData,
      bdmData
    };
  };

  // Update loading state
  useEffect(() => {
    const loading = performanceLoading || customerLoading || branchLoading || salesRepsLoading || visitNotesLoading || currentMonthLoading || previousMonthLoading || weeklyBranchLoading;
    setIsLoading(loading);
  }, [performanceLoading, customerLoading, branchLoading, salesRepsLoading, visitNotesLoading, currentMonthLoading, previousMonthLoading, weeklyBranchLoading]);

  const kpiData = processKPIData();
  const chartData = processChartData();
  

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl shadow-lg p-6">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-8 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl shadow-lg p-6">
                <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
                <div className="h-64 bg-gray-200 rounded"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-8">
      {/* KPI Cards */}
      <KPISection data={kpiData} />

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
        {/* Sales Trend Chart */}
        <SalesTrendChart 
          data={chartData?.salesTrendData || []} 
          period={selectedPeriod}
          onPeriodChange={setSelectedPeriod}
        />

        {/* Customer Performance Chart */}
        <CustomerPerformanceChart 
          data={chartData?.customerData || []} 
          period={selectedPeriod}
          onPeriodChange={setSelectedPeriod}
        />

        {/* Branch Performance Chart */}
        <BranchPerformanceChart 
          data={chartData?.branchData || []} 
          period={selectedPeriod}
          onPeriodChange={setSelectedPeriod}
        />

        {/* BDM Performance Chart */}
        <BDMPerformanceChart 
          data={chartData?.bdmData || []} 
          period={selectedPeriod}
          onPeriodChange={setSelectedPeriod}
        />
      </div>

      {/* Tables Section - COMMENTED OUT - See old dashboard at /dashboard-old */}
      {/* 
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
        {/* Top Customers Table */}
        {/* {chartData?.customerData && chartData.customerData.length > 0 && (
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Top 10 Customers</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rank</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Sales</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {chartData.customerData.slice(0, 10).map((customer, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        #{index + 1}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {customer.name}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-semibold text-gray-900">
                        £{customer.value.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Top Advisors/BDM Table */}
        {/* {chartData?.bdmData && chartData.bdmData.length > 0 && (
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Top 10 Sales Reps</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rank</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Branch</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Sales</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Visits</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {chartData.bdmData.slice(0, 10).map((rep, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        #{index + 1}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {rep.name}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {rep.branch || 'N/A'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-semibold text-gray-900">
                        £{rep.sales.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-500">
                        {rep.visits || 0}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Branch Performance Table */}
        {/* {chartData?.branchData && chartData.branchData.length > 0 && (
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Branch Performance</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rank</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Branch</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Sales</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {chartData.branchData.map((branch, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        #{index + 1}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {branch.name}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-semibold text-gray-900">
                        £{branch.value.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Daily Sales Trend Table */}
        {/* {chartData?.salesTrendData && chartData.salesTrendData.length > 0 && (
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 lg:col-span-2">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Daily Sales Trend (Last {chartData.salesTrendData.length} Days)</h3>
            <div className="overflow-x-auto max-h-96 overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Sales</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {chartData.salesTrendData
                    .slice()
                    .reverse() // Show most recent first
                    .map((day, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {new Date(day.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-semibold text-gray-900">
                        £{day.sales.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      */}
    </div>
  );
};

export default EnhancedDashboard;